import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Label,
  PrimaryButton,
  DefaultButton,
  TextField,
  ShimmeredDetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  ProgressIndicator,
  MessageBar,
  MessageBarType,
  IconButton,
  IButton,
  DialogType,
  getId,
  mergeStyles,
  Icon,
  Spinner,
  SpinnerSize,
  Checkbox,
  ILabelStyles,
  IStyleFunctionOrObject,
  ILabelStyleProps
} from '@fluentui/react';
import { useHistory, useParams } from 'react-router-dom';
import { msalAuth } from '../../shared/auth/MsalAuthProvider';
import { IUser } from '../../shared/models/User.model';
import { UIConstants } from '../../shared/models/Constants';
import ITenant from '../../shared/models/Tenant.model';
import { Utility } from '../../shared/models/Helper';
import AccessAPI from '../../store/AccessAPI';
import { DialogModel } from '../../shared/models/Dialog.model';
import { ModalDialog } from '../ModalDialog';
import { AttributeValue } from "../../shared/models/Role.model";

export interface AttributeStoreModel {
  id: string;
  tenantId: string;
  tenantName: string;
  attributes: Attribute[];
}

export interface Attribute {
  name: string;
  values: AttributeValue[];
  lastModifiedDate?: string;
  lastModifiedBy?: string;
}

export interface RestrictedAttribute {
  tenantName: string;
  attributeNames: string[];
}

// Configuration for restricted attributes - attributes listed here will have limited functionality
const RESTRICTED_ATTRIBUTES: RestrictedAttribute[] = [
  { 
    tenantName: UIConstants.Tenant.MyOrder, 
    attributeNames: ['BuyDesk', 'Group', 'Organization'] 
  },
  { 
    tenantName: UIConstants.Tenant.Mercury, 
    attributeNames: ['MWWAR', 'PWWAR', 'MXOS', 'PXOS', 'SCOMP', 'MCOMP','PCOMP'] 
  },
  {
    tenantName: UIConstants.Tenant.MercuryV3,
    attributeNames: ['MWWAR', 'PWWAR', 'MXOS', 'PXOS', 'SCOMP', 'MCOMP', 'PCOMP'] 
  },
  {
    tenantName: UIConstants.Tenant.Alfred,
    attributeNames: ['FY']
  }
];

const labelStyles: Partial<IStyleFunctionOrObject<ILabelStyleProps, ILabelStyles>> = {
  root: { marginTop: 7 }
};

const AttributeManagement: React.FC = () => {
  const { tenantName } = useParams<{ tenantName: string }>();

  // State
  const [userDetails, setUserDetails] = useState<IUser | null>(null);
  const [attributeStoreModel, setAttributeStoreModel] = useState<AttributeStoreModel | null>(null);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<MessageBarType>(MessageBarType.info);
  const [hideDialog, setHideDialog] = useState<boolean>(true);
  const [modalDialogContent, setModalDialogContent] = useState<DialogModel>({
    type: DialogType.normal,
    title: UIConstants.MessageBoxTitle.SaveAccess,
    closeButtonAriaLabel: UIConstants.ButtonText.Close,
    subText: UIConstants.Messages.SaveConfirmation,
    okAction: null,
    cancelAction: null,
  });
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [isTenantAdmin, setIsTenantAdmin] = useState<boolean>(false);
  const [selectedAttributeName, setSelectedAttributeName] = useState<string>('0');
  const [removedValues, setRemovedValues] = useState<string[]>([]);
  const [newlyAddedValues, setNewlyAddedValues] = useState<string>('');
  const [isTerms1Agreed, setIsTerms1Agreed] = useState<boolean>(false);
  const [isTerms2Agreed, setIsTerms2Agreed] = useState<boolean>(false);
  const [isValuesLoaded, setIsValuesLoaded] = useState<boolean>(false);
  const [displayedValues, setDisplayedValues] = useState<any[]>([]);
  const [isCreatingNewAttribute, setIsCreatingNewAttribute] = useState<boolean>(false);
  const [newAttributeName, setNewAttributeName] = useState<string>('');
  const [newAttributeValues, setNewAttributeValues] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isAttributeRestricted, setIsAttributeRestricted] = useState<boolean>(false);

  // Refs
  const tenantDataRef = useRef<ITenant | null>(null);
  const labelId = useRef<string>(getId('dialogLabel'));
  const subTextId = useRef<string>(getId('subTextLabel'));
  const alertMsgCloseBtnRef = useRef<IButton>(null);
  const createNewAttributeRef = useRef<() => Promise<void>>(async () => { });
  const createAttributeValuesRef = useRef<() => Promise<void>>(async () => { });
  const removeAttributeValuesRef = useRef<() => Promise<void>>(async () => { });

  /**
   * Check if the current attribute is restricted based on tenant name and attribute name
   */
  const isCurrentAttributeRestricted = useCallback((tenantName: string, attributeName: string): boolean => {
    return !!RESTRICTED_ATTRIBUTES.find(config => 
      config.tenantName.toLowerCase() === tenantName.toLowerCase() && 
      config.attributeNames.find(pattern => {
        const lowerAttributeName = attributeName.toLowerCase();
        const lowerPattern = pattern.toLowerCase();

        // Special handling for Alfred tenant - use contains check for FY
        // As Alfred data changes every fiscal year ()such as FY25, FY26), we are using this method instead of storing values for each year individually
        if (tenantName.toLowerCase() === UIConstants.Tenant.Alfred.toLowerCase() && lowerPattern === 'fy') {
          return lowerAttributeName.includes('fy');
        }

        // Exact match for all other patterns
        return lowerAttributeName === lowerPattern;
      })
    );
  }, []);

  /**
   * Show message to user
   */
  const showMessageToUser = useCallback((msg: string, msgType: MessageBarType = MessageBarType.info): void => {
    setShowMessage(true);
    setMessage(msg);
    setMessageType(msgType);

    setTimeout(() => {
      setShowMessage(false);
    }, 10000);

    setTimeout(() => {
      alertMsgCloseBtnRef.current?.focus();
    }, 100);
  }, []);

  /**
 * Load all attributes for the tenant
 */
  const loadAttributes = useCallback(async (attributeNameToReselect?: string): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await AccessAPI.getAttributesByTenantId(tenantDataRef.current!.TenantId);

      if (response && response.IsSuccess && response.Data) {
        const attributeStoreModelData: AttributeStoreModel = response.Data;

        // Transform AttributeStoreModel to Attribute for display
        const transformedAttributes: Attribute[] = attributeStoreModelData.attributes?.map(attr => ({
          name: attr.name,
          values: attr.values || [],
          lastModifiedDate: attr.lastModifiedDate,
          lastModifiedBy: attr.lastModifiedBy,
        })) || [];

        setAttributeStoreModel(attributeStoreModelData);
        setAttributes(transformedAttributes);

        // Reselect the attribute if specified
        if (attributeNameToReselect) {
          const attrToReselect = transformedAttributes.find(x => x.name === attributeNameToReselect);
          if (attrToReselect) {
            setSelectedAttributeName(attributeNameToReselect);
            setSelectedAttribute(attrToReselect);
            const valuesData = getAttributeValuesForDisplay(attrToReselect.values);
            setDisplayedValues(valuesData);
            setIsValuesLoaded(true);

            // Check if this attribute is restricted
            const isRestricted = isCurrentAttributeRestricted(tenantName, attributeNameToReselect);
            setIsAttributeRestricted(isRestricted);
          }
          else{
            setIsValuesLoaded(false);
            setSelectedAttributeName('0');
            setSelectedAttribute(null);
            setDisplayedValues([]);
            setIsAttributeRestricted(false);
          }
        }
      } else {
        setAttributes([]);
        setIsValuesLoaded(false);
        setSelectedAttributeName('0');
        setSelectedAttribute(null);
        setDisplayedValues([]);
        setIsAttributeRestricted(false);
      }
    } catch (error) {
      console.error('Error loading attributes:', error);
      showMessageToUser('Failed to load attributes. Please try again.', MessageBarType.error);
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  }, [showMessageToUser, isCurrentAttributeRestricted, tenantName]);

  /**
   * Close dialog
   */
  const closeDialog = useCallback((): void => {
    setHideDialog(true);
    setShowMessage(false);
    setIsTerms1Agreed(false);
    setIsTerms2Agreed(false);
    setNewAttributeName('');
    setNewAttributeValues('');
  }, []);

  /**
   * Handle attribute dropdown change
   */
  const onAttributeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>): void => {
    const selectedAttrName = event.target.value;

    if (selectedAttrName === '0') {
      setIsValuesLoaded(false);
      setSelectedAttributeName('0');
      setSelectedAttribute(null);
      setDisplayedValues([]);
      setIsAttributeRestricted(false);
    } else {
      setIsValuesLoaded(false);
      setSelectedAttributeName(selectedAttrName);
      const attr = attributes.find(x => x.name === selectedAttrName);

      if (attr) {
        setSelectedAttribute(attr);
        const valuesData = getAttributeValuesForDisplay(attr.values);
        setDisplayedValues(valuesData);
        setIsValuesLoaded(true);

        // Check if this attribute is restricted
        const isRestricted = isCurrentAttributeRestricted(tenantName, selectedAttrName);
        setIsAttributeRestricted(isRestricted);
      }
    }

    setSearchText('');
    setRemovedValues([]);
    setNewlyAddedValues('');
    setIsTerms1Agreed(false);
    setIsTerms2Agreed(false);
    setIsCreating(false);
    setIsCreatingNewAttribute(false);
  }, [attributes, isCurrentAttributeRestricted, tenantName]);

  /**
   * Get attribute values formatted for display
   */
  const getAttributeValuesForDisplay = (values: AttributeValue[]): any[] => {
    const valuesData = values.map(val => ({
      value: `Code: ${val.code} | Description: ${val.description}`
    }));

    return valuesData.sort((a, b) => {
      if (a.value.toUpperCase() < b.value.toUpperCase()) {
        return -1;
      } else if (a.value.toUpperCase() > b.value.toUpperCase()) {
        return 1;
      }
      return 0;
    });
  };

  /**
   * Validate search
   */
  const validateSearch = useCallback((): void => {
    if (selectedAttribute && searchText) {
      const filtered = selectedAttribute.values.filter(x =>
        x.code.toLowerCase().includes(searchText.toLowerCase()) ||
        x.description.toLowerCase().includes(searchText.toLowerCase())
      );
      const valuesData = getAttributeValuesForDisplay(filtered);
      setDisplayedValues(valuesData);
    } else if (selectedAttribute) {
      const valuesData = getAttributeValuesForDisplay(selectedAttribute.values);
      setDisplayedValues(valuesData);
    }
  }, [selectedAttribute, searchText]);

  /**
  * Refresh attribute values
  */
  const refreshAttributeValues = useCallback((): void => {
    setSearchText('');
    setRemovedValues([]);
    setNewlyAddedValues('');
    setIsTerms1Agreed(false);
    setIsTerms2Agreed(false);
    setIsCreating(false);
    setIsCreatingNewAttribute(false);

    // Reset displayed values to show all values of the selected attribute
    if (selectedAttribute) {
      const valuesData = getAttributeValuesForDisplay(selectedAttribute.values);
      setDisplayedValues(valuesData);
    }
  }, [selectedAttribute]);

  /**
   * Handle create new attribute click
   */
  const onCreateNewAttributeClick = useCallback((): void => {
    const msgText = "Please provide the attribute name and initial values in the below text boxes.";

    setModalDialogContent({
      subText: msgText,
      title: 'Create New Attribute',
      okButtonText: UIConstants.ButtonText.Ok,
      type: DialogType.normal,
      okAction: () => createNewAttributeRef.current(),
      cancelAction: closeDialog,
      closeMessageAction: closeDialog,
      closeButtonAriaLabel: UIConstants.ButtonText.Cancel
    });

    setHideDialog(false);
    setIsCreatingNewAttribute(true);
    setShowMessage(false);
  }, [closeDialog]);

  /**
   * Handle create new values click
   */
  const onCreateValuesClick = useCallback((): void => {
    const msgText = "Please add new attribute values in the below text box (semi-colon separated in format: code:description).";

    setModalDialogContent({
      subText: msgText,
      title: 'Create Attribute Values',
      okButtonText: UIConstants.ButtonText.Ok,
      type: DialogType.normal,
      okAction: () => createAttributeValuesRef.current(),
      cancelAction: closeDialog,
      closeMessageAction: closeDialog,
      closeButtonAriaLabel: UIConstants.ButtonText.Cancel,
    });

    setHideDialog(false);
    setIsCreating(true);
    setShowMessage(false);
  }, [closeDialog]);

  /**
   * Handle remove values click
   */
  const onRemoveValuesClick = useCallback((): void => {
        const msgText = "Are you sure you want to remove the selected attribute values? This action will not impact the access of any existing users.";

    setModalDialogContent({
      subText: msgText,
      title: 'Remove Attribute Values',
      okButtonText: UIConstants.ButtonText.Ok,
      type: DialogType.normal,
      okAction: () => removeAttributeValuesRef.current(),
      cancelAction: closeDialog,
      closeMessageAction: closeDialog,
      closeButtonAriaLabel: UIConstants.ButtonText.Cancel,
    });

    setHideDialog(false);
    setIsCreating(false);
    setShowMessage(false);
  }, [closeDialog]);

  /**
   * Create new attribute
   */
  const createNewAttribute = useCallback(async (): Promise<void> => {
    if (!isTerms1Agreed || !isTerms2Agreed) {
      setShowMessage(true);
      setMessage("Please agree to the attestations before proceeding.");
      return;
    }

    if (!newAttributeName || newAttributeName.trim() === '') {
      setShowMessage(true);
      setMessage("Please enter a valid attribute name.");
      return;
    }

    // Check if attribute already exists
    if (attributes.some(attr => attr.name.toLowerCase() === newAttributeName.trim().toLowerCase())) {
      setShowMessage(true);
      setMessage("An attribute with this name already exists. Please choose a different name.");
      return;
    }

    const valuesToAdd = newAttributeValues
      .split(';')
      .map(v => v.trim())
      .filter(v => v.length > 0 && v.includes(':'));

    if (valuesToAdd.length === 0) {
      setShowMessage(true);
      setMessage("Please enter at least one valid attribute value in format: code:description");
      return;
    }

    setIsSaving(true);

    try {
      // Parse attribute values
      const parsedValues: AttributeValue[] = valuesToAdd.map(line => {
        const [code, ...descriptionParts] = line.split(':');
        return {
          code: code.trim(),
          description: descriptionParts.join(':').trim()
        };
      });

      setHideDialog(true);

      // Call the backend API
      const response = await AccessAPI.CreateNewAttribute(
        tenantDataRef.current!.TenantId,
        newAttributeName.trim(),
        parsedValues
      );

      if (response.status === 200) {
        showMessageToUser(response.message, MessageBarType.success);
        refreshAttributeValues();
        await loadAttributes(newAttributeName.trim());
      } else {
        showMessageToUser(response.message, MessageBarType.warning);
      }
    } catch (error) {
      console.error('Error creating new attribute:', error);
      setShowMessage(true);
      setMessage('Failed to create new attribute. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [isTerms1Agreed, isTerms2Agreed, newAttributeName, newAttributeValues, attributes, showMessageToUser, loadAttributes, refreshAttributeValues]);
  useEffect(() => {
    createNewAttributeRef.current = createNewAttribute;
  }, [createNewAttribute]);
  /**
 * Create attribute values
 */
  const createAttributeValues = useCallback(async (): Promise<void> => {
    if (!isTerms1Agreed || !isTerms2Agreed) {
      setShowMessage(true);
      setMessage("Please agree to the attestations before proceeding.");
      return;
    }

    const valuesToAdd = newlyAddedValues
      .split(';')
      .map(v => v.trim())
      .filter(v => v.length > 0 && v.includes(':'));

    if (valuesToAdd.length === 0) {
      setShowMessage(true);
      setMessage("Please enter at least one valid attribute value in format: code:description");
      return;
    }

    setIsSaving(true);

    try {
      // Parse new values
      const parsedNewValues: AttributeValue[] = valuesToAdd.map(line => {
        const parts = line.split(':');
        const code = parts[0].trim();
        const description = parts.slice(1).join(':').trim();
        return {
          code: code,
          description: description
        };
      });

      // Prepare the request payload for the backend
      const request = {
        operation: 'Add',
        attributeValues: parsedNewValues,
        attributeCodes: []
      };
      setHideDialog(true);
      // Call the backend API
      var response = await AccessAPI.ManageAttributeValues(tenantDataRef.current!.TenantId, selectedAttribute!.name, request);

      if (response.status == 200) {
        const currentAttributeName = selectedAttribute!.name;
        setNewlyAddedValues('');
        setIsTerms1Agreed(false);
        setIsTerms2Agreed(false);
        setIsCreating(false);

        // Reload data and reselect the attribute
        await loadAttributes(currentAttributeName);
        showMessageToUser(response.message, MessageBarType.success);
      }
      else {
        showMessageToUser(response.message, MessageBarType.warning);
      }
    } catch (error) {
      console.error('Error creating attribute values:', error);
      setShowMessage(true);
      setMessage('Failed to create attribute values. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [isTerms1Agreed, isTerms2Agreed, newlyAddedValues, selectedAttribute, showMessageToUser, loadAttributes]);
  useEffect(() => {
    createAttributeValuesRef.current = createAttributeValues;
  }, [createAttributeValues]);

  /**
   * Remove attribute values
   */
  const removeAttributeValues = useCallback(async (): Promise<void> => {
    if (!isTerms1Agreed || !isTerms2Agreed) {
      setShowMessage(true);
      setMessage("Please agree to the attestations before proceeding.");
      return;
    }

    if (removedValues.length === 0) {
      setShowMessage(true);
      setMessage("Please select at least one attribute value to remove.");
      return;
    }
    setIsSaving(true);
    try {
      // Extract codes from the removed values (format: "Code: {code} | Description: {description}")
      const codesToRemove = removedValues.map(value => {
        const match = value.match(/^Code:\s*([^|]+)/);
        return match ? match[1].trim() : '';
      }).filter(code => code.length > 0);

      if (codesToRemove.length === 0) {
        setShowMessage(true);
        setMessage("Unable to extract codes from selected values.");
        return;
      }

      // Prepare the request payload for the backend
      const request = {
        operation: 'Remove',
        attributeValues: [],
        attributeCodes: codesToRemove
      };
      setHideDialog(true);
      // Call the backend API
      const response = await AccessAPI.ManageAttributeValues(tenantDataRef.current!.TenantId, selectedAttribute!.name, request);
      if (response.status == 200) {
        const currentAttributeName = selectedAttribute!.name;
        setRemovedValues([]);
        setIsTerms1Agreed(false);
        setIsTerms2Agreed(false);

        // Reload data and reselect the attribute
        await loadAttributes(currentAttributeName);
        showMessageToUser(response.message, MessageBarType.success);
      }
      else {
        showMessageToUser(response.message, MessageBarType.warning);
      }
    } catch (error) {
      console.error('Error removing attribute values:', error);
      setShowMessage(true);
      setMessage('Failed to remove attribute values. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [isTerms1Agreed, isTerms2Agreed, removedValues, selectedAttribute, showMessageToUser, loadAttributes]);
  useEffect(() => {
    removeAttributeValuesRef.current = removeAttributeValues;
  }, [removeAttributeValues]);
  /**
   * Get column definitions for the values list
   */
  const getColumns = useCallback((): IColumn[] => {
    const itemClass = mergeStyles({
      selectors: {
        '&:hover': {
          textDecoration: 'underline',
          cursor: 'pointer',
        },
      },
    });

    return [
      {
        key: 'remove',
        name: 'Remove',
        fieldName: 'remove',
        minWidth: 80,
        maxWidth: 80,
        isResizable: false,
        onRender: (item: any) => {
          return (
            <div className={itemClass}>
              <Checkbox
                checked={removedValues.includes(item.value)}
                disabled={isAttributeRestricted}
                onChange={(e, checked) => {
                  if (isAttributeRestricted) return;

                  let updatedRemovedValues = [...removedValues];
                  if (checked) {
                    updatedRemovedValues.push(item.value);
                  } else {
                    updatedRemovedValues = updatedRemovedValues.filter(v => v !== item.value);
                  }
                  setRemovedValues(updatedRemovedValues);
                }}
              />
            </div>
          );
        }
      },
      {
        key: 'value',
        name: 'Attribute Values',
        fieldName: 'value',
        minWidth: 500,
        maxWidth: 500,
        isResizable: true
      }
    ];
  }, [removedValues, isAttributeRestricted]);

  /**
   * Render message bar
   */
  const renderMessage = useCallback(() => {
    return (
      <div role="alert" aria-label={message}>
        <MessageBar
          key='AttributeMsg'
          style={{ fontSize: '.875rem' }}
          actions={
            <div>
              <IconButton
                componentRef={alertMsgCloseBtnRef}
                role="button"
                iconProps={{ iconName: 'Clear' }}
                title="Close"
                onClick={() => setShowMessage(false)}
              />
            </div>
          }
          messageBarType={messageType}
          isMultiline={false}
        >
          <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{message}</span>
        </MessageBar>
      </div>
    );
  }, [message, messageType]);

  // Component mount effect
  useEffect(() => {
    const initialize = async () => {
      const tenantData = Utility.GetTenantData().filter(x => x.TenantName.toLowerCase() === tenantName.toLowerCase())[0];
      tenantDataRef.current = tenantData;

      const userAccount = msalAuth.getAccount();
      const user: IUser = {
        PrincipalId: userAccount.accountIdentifier,
        Alias: userAccount.userName,
        Name: userAccount.name
      };

      setUserDetails(user);

      // Check if user is tenant admin
      const managementAccess = await AccessAPI.GetManagementAccess(tenantData.TenantId);
      setIsTenantAdmin(managementAccess.attributeManagement);

      await loadAttributes();
    };

    void initialize();
  }, [tenantName, loadAttributes]);

  if (isInitializing) {
  return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column' }}>
        <Spinner
          size={SpinnerSize.large}
          label="Loading attributes..."
          ariaLive="assertive"
          labelPosition="bottom"
        />
      </div>
    );
  }

  return (
    <React.Fragment>
      <div id={'div-msg-area'} style={{ height: 25 }}>
        {(showMessage && hideDialog) && renderMessage()}
        {isSaving && (
          <ProgressIndicator
            label={UIConstants.Messages.SubmitInProgressTitle}
            description={UIConstants.Messages.SubmitInProgress}
          />
        )}
      </div>

      <div id={'div-req-section'} style={{ padding: '1%', paddingTop: '2%' }}>
        <div key="grid-attribute-management" className={"ms-Grid"} dir="ltr">
          {/* Title */}
          <div key='grid-row-title' className={"ms-Grid-row"}>
            <div className={"ms-Grid-col ms-sm6 ms-md8 ms-lg10"}>
              <Label style={{ paddingLeft: 5, fontSize: 20 }}>
                <h1 role="heading" aria-level={1} className={"h1"} style={{ paddingLeft: 0, fontSize: 20, margin: 0 }}>
                  Attribute Management
                </h1>
              </Label>
            </div>
          </div>

          {/* Attribute Dropdown and Create New Button */}
          <div key='grid-row-attribute' className={"ms-Grid-row"}>
            <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg12"}>
              <div style={{ paddingLeft: 5, display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                <div>
                  <Label required={true} styles={labelStyles}>Attribute</Label>
                  <select
                    id={'drpAttribute'}
                    value={selectedAttributeName}
                    title="Attribute"
                    required={true}
                    onChange={onAttributeChange}
                    disabled={isLoading}
                    style={{ height: 32, width: window.innerWidth < 400 ? 250 : 350 }}
                  >
                    <option value="0">-- Select --</option>
                    {attributes.map(attr => (
                      <option key={attr.name} value={attr.name}>
                        {attr.name}
                      </option>
                    ))}
                  </select>
                </div>
                {isTenantAdmin && (
                  <DefaultButton
                    title="Create New Attribute"
                    onClick={onCreateNewAttributeClick}
                    disabled={isLoading || selectedAttributeName !== '0'}
                  >
                    <Icon iconName={'Add'} />&nbsp;Create New Attribute
                  </DefaultButton>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {isValuesLoaded && selectedAttributeName !== '0' && isTenantAdmin && (
            <div key='grid-row-actions' className={"ms-Grid-row"} style={{ paddingLeft: 5, paddingTop: 10 }}>
              <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg2"}>
                <span>
                  <TextField
                    placeholder="Search for Value"
                    value={searchText}
                    onChange={(ev, text) => setSearchText(text || '')}
                  />
                </span>
              </div>
              <span>
                <PrimaryButton title="Search" onClick={validateSearch}>
                  <Icon iconName={'Search'} />
                </PrimaryButton>
              </span>
              <span>&nbsp;&nbsp;</span>
              <DefaultButton title="Refresh" onClick={refreshAttributeValues}>
                <Icon iconName={'Refresh'} />&nbsp;Refresh
              </DefaultButton>
              <span>&nbsp;&nbsp;</span>
              <DefaultButton
                title="Create"
                disabled={removedValues.length > 0 || isAttributeRestricted}
                onClick={onCreateValuesClick}
              >
                <Icon iconName={'AddIn'} />&nbsp;Create New Values
              </DefaultButton>
              <span>&nbsp;&nbsp;</span>
              <DefaultButton
                title="Remove"
                disabled={removedValues.length <= 0 || isAttributeRestricted}
                onClick={onRemoveValuesClick}
              >
                <Icon iconName={'RemoveFromShoppingList'} />&nbsp;Remove Values
              </DefaultButton>
              <div className={"ms-Grid-col ms-sm12 ms-md12 ms-lg12"} style={{ paddingTop: 10 }}>
                {isAttributeRestricted && (
                  <MessageBar messageBarType={MessageBarType.warning} style={{ marginBottom: 10 }}>
                    Create and remove operations are disabled for this attribute.
                  </MessageBar>
                )}
                <Label>To create new value(s) - <span style={{ fontSize: 14, fontWeight: 400 }}>please click on 'Create New Values'.</span></Label>
                <Label>To remove existing value(s) - <span style={{ fontSize: 14, fontWeight: 400 }}>please select the corresponding value checkbox for which you want to remove - then click on 'Remove Values'.</span></Label>
              </div>
            </div>
          )}

          {/* Values List */}
          <div style={{ paddingLeft: 5 }} key="work-area">
            {isValuesLoaded ? (
              selectedAttribute && (
                <ShimmeredDetailsList
                  setKey="attribute-values"
                  columns={getColumns()}
                  items={displayedValues}
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                  enableShimmer={false}
                  ariaLabelForShimmer="Attribute values are being fetched"
                  ariaLabelForGrid="Attribute values"
                  listProps={{ renderedWindowsAhead: 10, renderedWindowsBehind: 10 }}
                />
              )
            ) : selectedAttributeName === '0' ? null : (
              <Spinner
                size={SpinnerSize.large}
                style={{ paddingLeft: 120, paddingTop: 30, alignItems: 'start' }}
                label="loading..."
                ariaLive="assertive"
                labelPosition="bottom"
              />
            )}
          </div>

          {/* Confirmation Dialog */}
          {!hideDialog && (
            <ModalDialog
              hidden={hideDialog}
              onDismiss={closeDialog}
              dialogContentProps={modalDialogContent}
              modalProps={{
                titleAriaId: labelId.current,
                subtitleAriaId: subTextId.current,
                isBlocking: false,
                styles: { main: { maxWidth: 450 } },
              }}
              showMessage={showMessage}
              message={message}
              content={
                modalDialogContent.okButtonText === UIConstants.ButtonText.Ok && (
                  <div>
                    {isCreatingNewAttribute && (
                      <>
                        <TextField
                          resizable={false}
                          disabled={isSaving}
                          placeholder="Enter attribute name (e.g., CompanyCode, Region)"
                          styles={{ root: { marginTop: 10 } }}
                          label="Attribute Name"
                          value={newAttributeName}
                          onChange={(ev, text) => setNewAttributeName(text || '')}
                          required
                        />
                        <TextField
                          resizable={false}
                          disabled={isSaving}
                          placeholder="Enter values (semi-colon separated in format: code:description)"
                          styles={{ root: { marginTop: 10 } }}
                          label="Initial Values"
                          multiline
                          rows={5}
                          value={newAttributeValues}
                          onChange={(ev, text) => setNewAttributeValues(text || '')}
                          required
                        />
                      </>
                    )}
                    {isCreating && !isCreatingNewAttribute && (
                      <TextField
                        resizable={false}
                        disabled={isSaving}
                        placeholder="Enter new values (semi-colon separated in format: code:description)"
                        styles={{ root: { marginTop: 10 } }}
                        label={UIConstants.ButtonText.Add}
                        multiline
                        rows={3}
                        value={newlyAddedValues}
                        onChange={(ev, text) => setNewlyAddedValues(text || '')}
                        required
                      />
                    )}
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <span>
                        <Checkbox
                          styles={{ root: { width: 20, marginTop: 10 } }}
                          checked={isTerms1Agreed}
                          onChange={(ev, checked) => setIsTerms1Agreed(!!checked)}
                        />
                      </span>
                      <span style={{ paddingLeft: 10 }}>
                        <Label style={{ fontWeight: "normal" }}>
                          <span>
                            By checking this box, I attest that I understand changing {isCreatingNewAttribute ? 'attributes' : 'attribute values'} will have a business impact.
                          </span>
                        </Label>
                      </span>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <span>
                        <Checkbox
                          styles={{ root: { width: 20, marginTop: 10 } }}
                          checked={isTerms2Agreed}
                          onChange={(ev, checked) => setIsTerms2Agreed(!!checked)}
                        />
                      </span>
                      <span style={{ paddingLeft: 10 }}>
                        <Label style={{ fontWeight: "normal" }}>
                          <span>
                            By checking this box, I attest that I have the business need and authority to make this change.
                          </span>
                        </Label>
                      </span>
                    </div>
                  </div>
                )
              }
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default AttributeManagement;
